"use client";

import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ImageSlider } from "@/features/products/ui/components/ImageSlider";
import { useSiteSettings } from "@/features/settings/ui/hooks/useSiteSettings";
import { Link } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { Image } from "@/lib/client/components/Image";
import { useShoppingCartStore } from "@/lib/client/stores";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { placeholderImage, placeholderPaths } from "@/lib/shared/constants";
import type { MediaDisplay } from "@/lib/shared/types/interfaces";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import type { Variant } from "@/payload-types";
import {
  AspectRatio,
  Box,
  Button,
  Container,
  cn,
  HStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Text,
  VStack,
} from "@/ui";
import { sumGiftSetMembersWithVariantsBase } from "../../shared/giftSetMemberPricing";
import type { GiftSet, GiftSetProduct } from "../../shared/types";

interface GiftSetDetailViewProps {
  giftSet: GiftSet;
}

function resolveVariant(product: GiftSetProduct["product"], variantId: string): Variant | null {
  const row = product.variants.find((v) => String(v.variant.id) === variantId);
  return row?.variant ?? null;
}

function resolveVariantRow(product: GiftSetProduct["product"], variantId: string) {
  return product.variants.find((v) => String(v.variant.id) === variantId) ?? null;
}

/** Matches PDP: only `stock === 0` is treated as out of stock. */
function isOutOfStock(stock: number | null | undefined): boolean {
  return stock === 0;
}

type GiftSetLineCartPayload =
  | { ok: true; variant: Variant; unitPrice: number }
  | { ok: false; error: "variant_required" | "out_of_stock" };

function getGiftSetLineCartPayload(
  line: GiftSetProduct,
  selectedVariantByRow: Record<string, string>,
): GiftSetLineCartPayload {
  const variantId = selectedVariantByRow[line.id];
  const variant = variantId ? resolveVariant(line.product, variantId) : null;
  if (!(variantId && variant)) {
    return { ok: false, error: "variant_required" };
  }
  const pv = resolveVariantRow(line.product, variantId);
  if (!pv || isOutOfStock(pv.stock)) {
    return { ok: false, error: "out_of_stock" };
  }
  return {
    ok: true,
    variant,
    unitPrice: pv.onSalePrice ?? pv.price ?? 0,
  };
}

export const GiftSetDetailView = ({ giftSet }: GiftSetDetailViewProps) => {
  const giftT = useTranslations("gift_sets");
  const generalT = useTranslations("general");
  const productDetailsT = useTranslations("product_details");
  const { country } = useCountryStore();
  const { imageRatio } = useSiteSettings();
  const { addItem, setIsOpen } = useShoppingCartStore();

  const productIdsKey = useMemo(
    () => giftSet.products.map((p) => p.product.id).join(","),
    [giftSet.products],
  );

  const [selectedVariantByRow, setSelectedVariantByRow] = useState<Record<string, string>>({});
  const [activeLineId, setActiveLineId] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const line of giftSet.products) {
      const rows = line.product.variants;
      const firstInStock = rows.find((pv) => !isOutOfStock(pv.stock));
      const pick = firstInStock ?? rows[0];
      if (pick) next[line.id] = String(pick.variant.id);
    }
    setSelectedVariantByRow(next);
    const firstLine = giftSet.products[0];
    setActiveLineId(firstLine?.id ?? null);
  }, [giftSet.id, productIdsKey]);

  const activeLine = useMemo(() => {
    const lines = giftSet.products;
    if (lines.length === 0) return null;
    const match = activeLineId ? lines.find((l) => l.id === activeLineId) : undefined;
    return match ?? lines[0] ?? null;
  }, [giftSet.products, activeLineId]);

  const activeVariantRow = useMemo(() => {
    if (!activeLine) return null;
    const vid = selectedVariantByRow[activeLine.id];
    if (!vid) return activeLine.product.variants[0] ?? null;
    return resolveVariantRow(activeLine.product, vid);
  }, [activeLine, selectedVariantByRow]);

  const galleryImages: MediaDisplay[] = useMemo(() => {
    if (!activeLine) return [];
    const imgs = activeLine.product.images;
    return imgs?.length ? imgs : [placeholderImage];
  }, [activeLine]);

  const galleryJumpIndex = useMemo(() => {
    if (!activeLine) return 0;
    const raw = activeVariantRow?.imageIndex;
    const n = typeof raw === "number" && !Number.isNaN(raw) ? raw : 0;
    const slideCount = galleryImages.length + (activeLine.product.video ? 1 : 0) || 1;
    const max = Math.max(0, slideCount - 1);
    return Math.max(0, Math.min(n, max));
  }, [activeLine, activeVariantRow?.imageIndex, galleryImages.length]);

  const comparisonTotalBase = useMemo(
    () => sumGiftSetMembersWithVariantsBase(giftSet.products, selectedVariantByRow),
    [giftSet.products, selectedVariantByRow],
  );

  const canAddAll = useMemo(() => {
    if (giftSet.products.length === 0) return false;
    for (const line of giftSet.products) {
      if (!getGiftSetLineCartPayload(line, selectedVariantByRow).ok) {
        return false;
      }
    }
    return true;
  }, [giftSet.products, selectedVariantByRow]);

  const displayPrice = giftSet.price;
  const hasDiscount = comparisonTotalBase > giftSet.price;
  const cover = giftSet.coverImage;
  const fallbackCoverSrc = (cover?.src ?? "").trim() || placeholderPaths.image;

  const trackAdd = useCallback(
    (product: GiftSetProduct["product"], variant: Variant, unitPrice: number) => {
      trackEvent(AnalyticsEvents.PRODUCT_ADDED_TO_CART, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        variant_id: variant.id,
        variant_name: variant.variant,
        quantity: 1,
        price: unitPrice,
        currency: country.currencyCode,
        gift_set_slug: giftSet.slug,
      });
    },
    [country.currencyCode, giftSet.slug],
  );

  const toastLineCartFailure = (error: "variant_required" | "out_of_stock") => {
    toast.error(
      error === "variant_required" ? giftT("detail_variant_required") : generalT("out_of_stock"),
    );
  };

  const handleAddLine = (line: GiftSetProduct) => {
    const result = getGiftSetLineCartPayload(line, selectedVariantByRow);
    if (!result.ok) {
      toastLineCartFailure(result.error);
      return;
    }
    trackAdd(line.product, result.variant, result.unitPrice);
    addItem(line.product, result.variant, 1);
    setIsOpen(true);
  };

  const handleAddAll = () => {
    const toAdd: {
      line: GiftSetProduct;
      variant: Variant;
      unitPrice: number;
    }[] = [];
    for (const line of giftSet.products) {
      const result = getGiftSetLineCartPayload(line, selectedVariantByRow);
      if (!result.ok) {
        toastLineCartFailure(result.error);
        return;
      }
      toAdd.push({
        line,
        variant: result.variant,
        unitPrice: result.unitPrice,
      });
    }
    for (const { line, variant, unitPrice } of toAdd) {
      trackAdd(line.product, variant, unitPrice);
      addItem(line.product, variant, 1);
    }
    setIsOpen(true);
  };

  return (
    <section className="section-spacing">
      <Container>
        <Link
          href="/gift-sets"
          className="mb-10 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          <ArrowLeft
            className="size-4"
            aria-hidden
          />
          {giftT("detail_back")}
        </Link>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="space-y-4">
            {activeLine ? (
              <>
                <div
                  className="overflow-hidden rounded-lg border border-border/25 bg-linear-to-b from-muted/20 via-background to-muted/10 p-1.5 shadow-lg shadow-black/6 ring-1 ring-inset ring-foreground/4 dark:shadow-black/40 dark:ring-foreground/8"
                  role="region"
                  aria-label={giftT("detail_gallery_aria")}
                >
                  <ImageSlider
                    key={activeLine.id}
                    images={galleryImages}
                    video={activeLine.product.video}
                    hasThumb
                    jumpToIndex={galleryJumpIndex}
                    width={900}
                    ratio={imageRatio}
                    fallbackAlt={activeLine.product.name ?? giftSet.displayTitle}
                    className="gap-3"
                  />
                </div>
                {giftSet.products.length > 1 ? (
                  <ul
                    className="flex flex-wrap gap-2"
                    aria-label={giftT("detail_gallery_aria")}
                  >
                    {giftSet.products.map((line) => {
                      const thumb = line.product.images?.[0] ?? placeholderImage;
                      const isActive = line.id === activeLine.id;
                      return (
                        <li key={line.id}>
                          <button
                            type="button"
                            onClick={() => setActiveLineId(line.id)}
                            className={cn(
                              "relative size-16 shrink-0 overflow-hidden rounded-md outline-none transition-[box-shadow,opacity,ring-color] duration-200 ease-out",
                              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                              isActive
                                ? "z-1 ring-2 ring-foreground/75 ring-offset-2 ring-offset-background shadow-md shadow-black/10 dark:shadow-black/50"
                                : "border border-border/40 opacity-[0.92] hover:border-foreground/25 hover:opacity-100 hover:shadow-sm",
                            )}
                            aria-pressed={isActive}
                            aria-label={giftT("detail_preview_piece_aria", {
                              name: line.product.name ?? "",
                            })}
                          >
                            <Image
                              src={(thumb.src ?? "").trim() || placeholderPaths.image}
                              alt={thumb.alt ?? line.product.name ?? ""}
                              width={64}
                              height={64}
                              className="size-full object-cover object-center"
                            />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </>
            ) : (
              <AspectRatio
                className="overflow-hidden rounded-sm border border-border/40 bg-muted/20 shadow-sm"
                ratio={imageRatio}
              >
                <Image
                  src={fallbackCoverSrc}
                  alt={cover?.alt ?? giftSet.displayTitle}
                  fill
                  quality={80}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover object-center"
                  priority
                />
              </AspectRatio>
            )}
          </div>

          <VStack className="gap-8">
            <VStack className="gap-3">
              <span className="section-label">{giftT("label")}</span>
              <h1 className="font-product-title text-balance text-3xl font-medium leading-tight text-foreground sm:text-4xl lg:text-5xl">
                {giftSet.displayTitle}
              </h1>
            </VStack>

            <Box>
              {hasDiscount ? (
                <HStack className="flex-wrap items-baseline gap-3">
                  <span className="font-price text-lg text-muted-foreground line-through">
                    {country.symbol}
                    {Number(comparisonTotalBase * country.exchangeRate).toFixed(2)}
                  </span>
                  <span className="font-price text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                    {country.symbol}
                    {Number(displayPrice * country.exchangeRate).toFixed(2)}
                  </span>
                </HStack>
              ) : (
                <span className="font-price text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                  {country.symbol}
                  {Number(displayPrice * country.exchangeRate).toFixed(2)}
                </span>
              )}
            </Box>

            <VStack className="gap-3">
              <Button
                type="button"
                onClick={handleAddAll}
                disabled={!canAddAll}
                className="w-full rounded-none bg-foreground py-5 text-[11px] font-medium uppercase tracking-[0.15em] text-background transition-all duration-500 hover:bg-primary hover:text-primary-foreground hover:shadow-gold disabled:pointer-events-none disabled:opacity-50"
              >
                {giftT("detail_add_all_to_cart")}
              </Button>
            </VStack>

            <Separator className="bg-border/60" />

            <VStack className="gap-4">
              <h2 className="font-serif text-lg font-medium tracking-tight text-foreground">
                {giftT("detail_whats_inside")}
              </h2>
              <ul className="divide-y divide-border/50 border border-border/40">
                {giftSet.products.map((line) => {
                  const variantId = selectedVariantByRow[line.id];
                  const variantOptions = line.product.variants;
                  const isActiveLine = activeLine?.id === line.id;
                  const selectedPv = variantId ? resolveVariantRow(line.product, variantId) : null;
                  const selectedLineOutOfStock =
                    selectedPv != null && isOutOfStock(selectedPv.stock);
                  return (
                    <li
                      key={line.id}
                      className={cn(
                        "group flex cursor-pointer flex-col gap-3 px-4 py-4 transition-colors",
                        isActiveLine && "bg-muted/25",
                      )}
                      onClick={() => setActiveLineId(line.id)}
                      aria-current={isActiveLine ? "true" : undefined}
                    >
                      <HStack className="items-start justify-between gap-4">
                        <VStack className="min-w-0 flex-1 gap-1">
                          {isActiveLine ? (
                            <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                              {giftT("detail_active_piece")}
                            </span>
                          ) : null}
                          <Text
                            className={cn(
                              "font-product-title font-medium text-foreground",
                              isActiveLine && "text-primary",
                            )}
                          >
                            {line.product.name}
                          </Text>
                          <Link
                            href={`/products/${line.product.sku}`}
                            className="text-xs text-muted-foreground underline-offset-2 w-fit hover:text-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {productDetailsT("view_full_details")}
                          </Link>
                          {variantOptions.length > 1 ? (
                            <div
                              className="mt-1 max-w-xs"
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              <Select
                                value={variantId}
                                onValueChange={(value) => {
                                  setSelectedVariantByRow((prev) => ({
                                    ...prev,
                                    [line.id]: value,
                                  }));
                                  setActiveLineId(line.id);
                                }}
                              >
                                <SelectTrigger
                                  className={cn(
                                    "w-full",
                                    selectedLineOutOfStock && "text-muted-foreground",
                                  )}
                                >
                                  <SelectValue placeholder={giftT("detail_choose_variant")} />
                                </SelectTrigger>
                                <SelectContent position="popper">
                                  {variantOptions.map((pv) => (
                                    <SelectItem
                                      key={pv.variant.id}
                                      value={String(pv.variant.id)}
                                      disabled={isOutOfStock(pv.stock)}
                                    >
                                      {translateProductVariantLabel(
                                        productDetailsT,
                                        pv.variant.variant,
                                      )}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {selectedLineOutOfStock ? (
                                <p
                                  className="mt-1 text-xs text-muted-foreground"
                                  role="status"
                                >
                                  {generalT("out_of_stock")}
                                </p>
                              ) : null}
                            </div>
                          ) : variantOptions[0] ? (
                            <VStack className="gap-1">
                              <span className="text-sm text-muted-foreground">
                                {translateProductVariantLabel(
                                  productDetailsT,
                                  variantOptions[0].variant.variant,
                                )}
                              </span>
                              {isOutOfStock(variantOptions[0].stock) ? (
                                <p
                                  className="text-xs text-muted-foreground"
                                  role="status"
                                >
                                  {generalT("out_of_stock")}
                                </p>
                              ) : null}
                            </VStack>
                          ) : null}
                        </VStack>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={selectedLineOutOfStock}
                          className="shrink-0 rounded-none text-[10px] font-medium uppercase tracking-[0.12em] disabled:pointer-events-none disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddLine(line);
                          }}
                        >
                          {generalT("add_to_cart")}
                        </Button>
                      </HStack>
                    </li>
                  );
                })}
              </ul>
            </VStack>

            <p className="text-sm leading-relaxed text-muted-foreground">
              {giftT("detail_bundle_note")}
            </p>
          </VStack>
        </div>
      </Container>
    </section>
  );
};
